import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { forwardRef, useState, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { styled } from 'styled-components';
import { pageResourceEmbeds } from '../expandedResource.config.js';
import { IconWorld } from '@fabric-app/icons/old';
import { useResourceBookmarkDataSafe } from '../../resource-detail/components/context/resourceDataContext.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Flex } from '@fabric-app/ui/components/Flex';
import StateSpinner from '../../resources/components/StateSpinner.js';
import { P } from '@fabric-app/ui/components/Typography';
import { Image } from '@fabric-app/ui/components/Image';
import { Void } from '@fabric-app/ui/components/Void';
import { mediaHover, mediaNotHover } from '@fabric-app/ui/constants/styled-utils';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import useMutationUpdateResourceLinkPreview from '../../resource-detail/mutations/useMutationUpdateResourceLinkPreview.js';
import getLinkPreviewImageAspectRatio from '../../resource-detail/utils/getLinkPreviewImageAspectRatio.js';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { getImageNaturalDimensions } from '@fabric-app/utils/files';
import { AbsolutePosition } from '@fabric-app/ui/components/AbsolutePosition';
import { Button } from '@fabric-app/ui/components/Button';
import { useTextAreaAutoResize } from '@fabric-app/ui/utils/useTextAreaAutoResize';
import { useDroppable } from '@fabric-app/ui/utils/useDroppable';
import TakeOrPickImage from '../../mediaRecording/components/TakeOrPickImage.js';
import { IconPencil2, IconDropHere } from '@fabric-app/icons/svg';
import { useQueryResourceTranscript } from '../../resource-detail/queries/useQueryResourceTranscript.js';
import { isDefined } from '@fabric-app/utils/guards';
import { Transcript } from '../../mediaPlaying/components/Transcript.js';

const MetadataImageContainer = styled.div.withConfig({
  displayName: "PageResourceMetadata__MetadataImageContainer",
  componentId: "sc-77c9d26d-0"
})([
  `position:relative;border:1px solid `,
  `;background-color:`,
  `;border-radius:14px;overflow:hidden;padding:10px;> [data-edit-button]{pointer-events:none;opacity:0;}`,
  `{&:hover,&:active{> [data-edit-button]{pointer-events:auto;opacity:1;}}}`,
  `{&:focus,&:focus-within{> [data-edit-button]{pointer-events:auto;opacity:1;}}}`
], cssVar["color-border-primary"], cssVar["color-bg-primary"], mediaHover, mediaNotHover);
const MetadataImageMissingContainer = styled(Flex).attrs({
  direction: "column",
  alignItems: "center",
  justifyContent: "center",
  flexGrow: 1
}).withConfig({
  displayName: "PageResourceMetadata__MetadataImageMissingContainer",
  componentId: "sc-77c9d26d-1"
})([
  `height:300px;max-height:300px;padding:1rem;border-radius:6px;border:1px dashed rgba(`,
  `,0.2);cursor:pointer;opacity:0.7;transition:opacity 0.2s ease;will-change:opacity;`,
  `{&:hover{opacity:1;}}&[data-drop-active='true']{opacity:1;}`
], cssVar["color-text-primary-rgb"], mediaHover);
const MetadataImageMissingInnerContainer = styled(Flex).attrs({
  direction: "column",
  gap: "sections",
  alignItems: "center",
  justifyContent: "center",
  flexGrow: 1
}).withConfig({
  displayName: "PageResourceMetadata__MetadataImageMissingInnerContainer",
  componentId: "sc-77c9d26d-2"
})([
  `width:100px;max-width:100px;color:rgba(`,
  `,0.39);text-align:center;> svg{color:rgba(`,
  `,0.234);}`
], cssVar["color-text-primary-rgb"], cssVar["color-text-primary-rgb"]);
const MetadataImageEmptyContainer = styled(Flex).attrs({
  direction: "column",
  gap: "elementsContainer",
  alignItems: "center",
  justifyContent: "center",
  flexGrow: 1
}).withConfig({
  displayName: "PageResourceMetadata__MetadataImageEmptyContainer",
  componentId: "sc-77c9d26d-3"
})([
  `padding:1rem;background-color:`,
  `;border-radius:6px;max-height:300px;`
], cssVar["color-bg-quarternary"]);
const MetadataDescription = styled(Flex).attrs({
  direction: "row",
  gap: "elementsContainer",
  alignItems: "center"
}).withConfig({
  displayName: "PageResourceMetadata__MetadataDescription",
  componentId: "sc-77c9d26d-4"
})([
  `padding:1rem;background-color:`,
  `;border-radius:6px;`
], cssVar["color-bg-primary"]);
const Container = styled(Flex).attrs({
  direction: "column",
  gap: "elementsContainer",
  flexGrow: 1
}).withConfig({
  displayName: "PageResourceMetadata__Container",
  componentId: "sc-77c9d26d-5"
})([
  `width:100%;max-width:520px;`
]);
const DescriptionTextArea = styled.textarea.withConfig({
  displayName: "PageResourceMetadata__DescriptionTextArea",
  componentId: "sc-77c9d26d-6"
})([
  `color:`,
  `;width:100%;border:none;background:transparent;outline:none;resize:none;`
], cssVar["color-text-tertiary"]);
const PageResourceMetadataProcessing = /* @__PURE__ */ forwardRef((_, ref) => {
  return /* @__PURE__ */ jsxs(Container, {
    ref,
    children: [
      /* @__PURE__ */ jsxs(MetadataImageEmptyContainer, {
        children: [
          /* @__PURE__ */ jsx(StateSpinner, {
            size: 22,
            thickness: 3
          }),
          /* @__PURE__ */ jsx(P, {
            color: "tertiary",
            size: "sm",
            children: "Fetching website image"
          })
        ]
      }),
      /* @__PURE__ */ jsxs(MetadataDescription, {
        children: [
          /* @__PURE__ */ jsx(StateSpinner, {
            size: 22,
            thickness: 3
          }),
          /* @__PURE__ */ jsx(P, {
            color: "tertiary",
            size: "sm",
            children: "Fetching website metadata"
          })
        ]
      })
    ]
  });
});
PageResourceMetadataProcessing.displayName = "PageResourceMetadataProcessing";
const PageResourceMetadataProcessed = /* @__PURE__ */ forwardRef((_, ref) => {
  const { resource, disabledActions } = useResourceBookmarkDataSafe();
  const { isMobileView } = useResponsive();
  const { transcriptLines } = useQueryResourceTranscript(resource);
  const { mutate: mutateUpdateResourceLinkPreview } = useMutationUpdateResourceLinkPreview(resource.id);
  const originUrl = resource.originUrl || resource.data.webpage?.url || "";
  const embed = pageResourceEmbeds.find((embed2) => embed2.matchFn(resource.originUrl || resource.data.webpage?.url || ""));
  const [imageFailed, setImageFailed] = React__default.useState(false);
  const coverUrl = resource.thumbnail?.lg ?? resource.data.webpage?.image?.url;
  const [imageAspectRatio, setImageAspectRatio] = useState(getLinkPreviewImageAspectRatio(resource.data.webpage));
  const previousCoverUrl = useRef(coverUrl);
  const title = resource.name;
  const imageFileRef = useRef(null);
  const imageInput = useInputControls(coverUrl, {
    debounce: 500,
    onChange: (newImageUrl) => {
      if (!imageFileRef.current) return;
      mutateUpdateResourceLinkPreview({
        resource: {
          id: resource.id,
          kind: "bookmark"
        },
        newData: {
          image: {
            file: imageFileRef.current,
            url: newImageUrl
          }
        }
      }, {
        onSuccess: () => {
          imageFileRef.current = null;
        }
      });
    }
  });
  const imageInputSet = imageInput.set;
  useEffect(() => {
    if (previousCoverUrl.current === coverUrl) return;
    previousCoverUrl.current = coverUrl;
    imageInputSet(coverUrl ?? "");
    setImageAspectRatio(getLinkPreviewImageAspectRatio(resource.data.webpage));
    setImageFailed(false);
  }, [
    coverUrl,
    imageInputSet,
    resource.data.webpage,
    setImageAspectRatio
  ]);
  const textAreaRef = useRef(null);
  const descriptionInput = useInputControls(resource.data.webpage?.description ?? "", {
    debounce: 500,
    shouldUpdateStateOnDefaultValueChange: () => {
      return document.activeElement !== textAreaRef.current;
    },
    onChange: (newDescription) => mutateUpdateResourceLinkPreview({
      resource: {
        id: resource.id,
        kind: "bookmark"
      },
      newData: {
        description: newDescription
      }
    })
  });
  useTextAreaAutoResize(textAreaRef, descriptionInput.value, {
    minHeight: 17,
    maxHeight: Infinity
  });
  const hasValidImage = !!imageInput.value && !imageFailed;
  const isEditable = !disabledActions.modify;
  const EmbedElement = embed?.renderElement ?? Void;
  const showingImage = hasValidImage || isEditable;
  const showingDescription = !!descriptionInput.sanitizedValue || isEditable;
  const imageInputValueRef = useRef(imageInput.value);
  imageInputValueRef.current = imageInput.value;
  const onChangeImage = async (item) => {
    const file = item instanceof File ? item : item?.getAsFile();
    if (!file || !file.type.startsWith("image/")) {
      return;
    }
    setImageFailed(false);
    imageFileRef.current = file;
    try {
      URL.revokeObjectURL(imageInputValueRef.current);
    } catch (_e) {
    }
    const url = URL.createObjectURL(file);
    const imageAspectRatio2 = await getImageNaturalDimensions(url);
    setImageAspectRatio(imageAspectRatio2.width / imageAspectRatio2.height);
    imageInput.onChange(url);
  };
  const onSetImages = (images) => onChangeImage(images[0]);
  const { draggingState, dragEvents } = useDroppable({
    onDrop: (e) => onChangeImage(e.dataTransfer.items[0]),
    accept: (item) => item.kind === "file" && item.type.startsWith("image/"),
    maxFiles: 1
  });
  return /* @__PURE__ */ jsxs(Container, {
    ref,
    children: [
      /* @__PURE__ */ jsxs(EmbedElement, {
        url: originUrl,
        children: [
          hasValidImage ? /* @__PURE__ */ jsxs(MetadataImageContainer, {
            tabIndex: isMobileView ? 0 : -1,
            children: [
              isEditable && /* @__PURE__ */ jsx(TakeOrPickImage, {
                onSetImages,
                limit: 1,
                renderTriggerElement: ({ onOpenChange }) => /* @__PURE__ */ jsx(AbsolutePosition, {
                  top: 14,
                  right: 14,
                  asChild: true,
                  children: /* @__PURE__ */ jsx(Button, {
                    shape: "round",
                    variant: "bg-primary",
                    "data-edit-button": true,
                    onClick: () => onOpenChange(true),
                    children: /* @__PURE__ */ jsx(IconPencil2, {
                      style: {
                        width: 24
                      }
                    })
                  })
                })
              }),
              /* @__PURE__ */ jsx(Image, {
                src: imageInput.value,
                alt: title || "Webpage preview",
                fullWidth: true,
                onError: () => setImageFailed(true),
                style: {
                  aspectRatio: imageAspectRatio ?? void 0
                }
              })
            ]
          }) : isEditable ? /* @__PURE__ */ jsx(TakeOrPickImage, {
            onSetImages,
            limit: 1,
            renderTriggerElement: ({ onOpenChange }) => /* @__PURE__ */ jsx(MetadataImageMissingContainer, {
              ...dragEvents,
              "data-drop-active": draggingState === "valid",
              onClick: () => onOpenChange(true),
              children: /* @__PURE__ */ jsxs(MetadataImageMissingInnerContainer, {
                children: [
                  /* @__PURE__ */ jsx(IconDropHere, {
                    style: {
                      width: 50,
                      height: "auto"
                    }
                  }),
                  /* @__PURE__ */ jsx(P, {
                    color: "inherit",
                    size: "sm",
                    weight: 500,
                    children: "Click to upload an image"
                  })
                ]
              })
            })
          }) : null,
          !showingImage && !showingDescription && /* @__PURE__ */ jsxs(Flex, {
            direction: "column",
            gap: "elementsContainer",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: true,
            children: [
              /* @__PURE__ */ jsx(P, {
                color: "tertiary",
                children: /* @__PURE__ */ jsx(IconWorld, {
                  style: {
                    width: 64,
                    height: 64
                  }
                })
              }),
              /* @__PURE__ */ jsx(P, {
                color: "tertiary",
                size: "sm",
                children: "No link preview"
              })
            ]
          })
        ]
      }),
      !!descriptionInput.sanitizedValue || isEditable ? /* @__PURE__ */ jsx(MetadataDescription, {
        children: /* @__PURE__ */ jsx(Flex, {
          flexGrow: 1,
          children: /* @__PURE__ */ jsx(DescriptionTextArea, {
            ref: textAreaRef,
            value: descriptionInput.value,
            onChange: descriptionInput.onChange,
            placeholder: "Add a page description",
            rows: 1,
            readOnly: !isEditable
          })
        })
      }) : null,
      isDefined(transcriptLines) && /* @__PURE__ */ jsx(Transcript, {
        transcriptLines
      })
    ]
  });
});
PageResourceMetadataProcessed.displayName = "PageResourceMetadataProcessed";
const PageResourceMetadata = /* @__PURE__ */ forwardRef((_, ref) => {
  const { resource } = useResourceBookmarkDataSafe();
  const oneHourAgo = Date.now() - 60 * 60 * 1e3;
  if (!resource.data.webpage && dayjs(resource.createdAt).isAfter(oneHourAgo)) {
    return /* @__PURE__ */ jsx(PageResourceMetadataProcessing, {
      ref
    });
  } else {
    return /* @__PURE__ */ jsx(PageResourceMetadataProcessed, {
      ref
    });
  }
});
PageResourceMetadata.displayName = "PageResourceMetadata";

export { PageResourceMetadata };
//# sourceMappingURL=PageResourceMetadata.js.map
